import { useState, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useSearchParams } from "react-router-dom";
import CreateAdWizard from "../components/CreateAdWizard";
import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid, Legend, BarChart, Bar,
} from "recharts";
import {
  Megaphone, Link2, RefreshCw, Play, Pause, Facebook, Instagram,
  BarChart3, Rocket, CheckCircle2, Loader2, Plus, Sparkles, Undo2, X, ShieldAlert, History,
  ChevronDown, ChevronRight, Pencil,
} from "lucide-react";

const DATE_PRESETS = [
  ["today", "Today"], ["yesterday", "Yesterday"], ["last_7d", "Last 7 days"],
  ["last_14d", "Last 14 days"], ["last_30d", "Last 30 days"], ["last_90d", "Last 90 days"],
];
const LEVELS = [["account", "Account"], ["campaign", "Campaign"], ["adset", "Ad Set"], ["ad", "Ad"]];

const fmtMoney = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const fmtNum = (v) => (v == null ? "—" : Number(v).toLocaleString());
const sumField = (rows, f) => rows.reduce((a, r) => a + (parseFloat(r[f]) || 0), 0);

export default function MetaAds() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [account, setAccount] = useState("");
  const [datePreset, setDatePreset] = useState("last_7d");
  const [level, setLevel] = useState("campaign");
  const [insights, setInsights] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [tab, setTab] = useState("overview");
  const [recs, setRecs] = useState([]);
  const [aiSummary, setAiSummary] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [activity, setActivity] = useState([]);
  const [trend, setTrend] = useState(null);
  const [expandedCamp, setExpandedCamp] = useState(null);
  const [adsets, setAdsets] = useState({});
  const [expandedAdset, setExpandedAdset] = useState(null);
  const [ads, setAds] = useState({});
  const [editObj, setEditObj] = useState(null);
  const [aiForecast, setAiForecast] = useState(null);
  const [forecasting, setForecasting] = useState(false);
  const [breakdownType, setBreakdownType] = useState("publisher_platform");
  const [breakdownRows, setBreakdownRows] = useState([]);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  const load = useCallback(async () => {
    try {
      const [cfg, st] = await Promise.all([
        axios.get(`${API}/meta/config`),
        axios.get(`${API}/meta/status`),
      ]);
      setConfig(cfg.data);
      setStatus(st.data);
      const sel = st.data.selected_account_ids || [];
      const first = sel[0] || (st.data.ad_accounts || [])[0]?.id;
      if (first) setAccount(first);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Meta Ads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onFocus = () => { load(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  useEffect(() => {
    if (searchParams.get("meta_connected")) {
      toast.success("Meta Ads connected successfully!");
      searchParams.delete("meta_connected"); setSearchParams(searchParams, { replace: true });
    }
    const err = searchParams.get("meta_error");
    if (err) {
      toast.error(err === "denied" ? "Meta connection was cancelled." : "Meta connection failed. Please try again.");
      searchParams.delete("meta_error"); setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadData = useCallback(async () => {
    if (!account) return;
    setLoadingData(true);
    try {
      const [ins, camp] = await Promise.all([
        axios.get(`${API}/meta/ad-accounts/${account}/insights`, { params: { level, date_preset: datePreset } }),
        axios.get(`${API}/meta/ad-accounts/${account}/campaigns`),
      ]);
      setInsights(ins.data.insights || []);
      setCampaigns(camp.data.campaigns || []);
      axios.get(`${API}/meta/ad-accounts/${account}/trend`, { params: { date_preset: datePreset } })
        .then((t) => setTrend(t.data)).catch(() => setTrend(null));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load ad data");
    } finally {
      setLoadingData(false);
    }
  }, [account, level, datePreset]);

  useEffect(() => { if (status?.connected && account) loadData(); }, [status, account, level, datePreset, loadData]);

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/meta/oauth/start`);
      // The app may run inside an iframe (preview shell); Facebook refuses to load in an
      // iframe, so open the OAuth dialog in a top-level tab.
      const w = window.open(res.data.url, "_blank", "noopener");
      if (!w) {
        // Popup blocked — break out of the iframe to the top window instead.
        try { window.top.location.href = res.data.url; }
        catch (_) { window.location.href = res.data.url; }
        return;
      }
      toast.info("Continue in the new tab, then come back here — we'll refresh automatically.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Meta connection");
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect your Meta Ads account?")) return;
    try {
      await axios.delete(`${API}/meta/connection`);
      toast.success("Disconnected from Meta");
      load();
    } catch (e) { toast.error("Failed to disconnect"); }
  };

  const toggleStatus = async (obj) => {
    const next = (obj.effective_status === "ACTIVE" || obj.status === "ACTIVE") ? "PAUSED" : "ACTIVE";
    try {
      await axios.post(`${API}/meta/objects/${obj.id}/status`, null, { params: { status: next } });
      toast.success(`Campaign ${next === "ACTIVE" ? "resumed" : "paused"}`);
      setCampaigns((cs) => cs.map((c) => c.id === obj.id ? { ...c, status: next, effective_status: next } : c));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update status"); }
  };

  const runAnalysis = async () => {
    if (!account) return;
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/meta/ad-accounts/${account}/ai-recommendations`, null, { params: { date_preset: datePreset } });
      setAiSummary(res.data.summary || "");
      setRecs(res.data.recommendations || []);
      toast.success("AI analysis complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI analysis failed");
    } finally { setAnalyzing(false); }
  };

  const loadRecs = useCallback(async () => {
    if (!account) return;
    try {
      const res = await axios.get(`${API}/meta/recommendations`, { params: { account_id: account } });
      setRecs(res.data.recommendations || []);
    } catch (e) { /* ignore */ }
  }, [account]);

  const loadActivity = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/meta/activity`);
      setActivity(res.data.activity || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { if (tab === "ai") loadRecs(); if (tab === "activity") loadActivity(); }, [tab, loadRecs, loadActivity]);

  const applyRec = async (rec) => {
    try {
      await axios.post(`${API}/meta/recommendations/${rec.id}/apply`);
      toast.success("Applied — you can roll it back anytime");
      loadRecs(); loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply"); }
  };
  const rollbackRec = async (rec) => {
    try {
      await axios.post(`${API}/meta/recommendations/${rec.id}/rollback`);
      toast.success("Rolled back to previous settings");
      loadRecs(); loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to roll back"); }
  };
  const dismissRec = async (rec) => {
    try { await axios.post(`${API}/meta/recommendations/${rec.id}/dismiss`); loadRecs(); } catch (e) { /* ignore */ }
  };

  const setObjStatus = async (id, currentStatus, kind, parentId) => {
    const next = currentStatus === "ACTIVE" ? "PAUSED" : "ACTIVE";
    try {
      await axios.post(`${API}/meta/objects/${id}/status`, null, { params: { status: next } });
      toast.success(next === "ACTIVE" ? "Resumed" : "Paused");
      if (kind === "adset") setAdsets((m) => ({ ...m, [parentId]: (m[parentId] || []).map((a) => a.id === id ? { ...a, status: next, effective_status: next } : a) }));
      if (kind === "ad") setAds((m) => ({ ...m, [parentId]: (m[parentId] || []).map((a) => a.id === id ? { ...a, status: next, effective_status: next } : a) }));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const toggleCampaign = async (c) => {
    if (expandedCamp === c.id) { setExpandedCamp(null); return; }
    setExpandedCamp(c.id);
    if (!adsets[c.id]) {
      try {
        const res = await axios.get(`${API}/meta/ad-accounts/${account}/adsets`, { params: { campaign_id: c.id } });
        setAdsets((m) => ({ ...m, [c.id]: res.data.adsets || [] }));
      } catch (e) { toast.error("Failed to load ad sets"); }
    }
  };

  const toggleAdset = async (a) => {
    if (expandedAdset === a.id) { setExpandedAdset(null); return; }
    setExpandedAdset(a.id);
    if (!ads[a.id]) {
      try {
        const res = await axios.get(`${API}/meta/ad-accounts/${account}/ads`, { params: { adset_id: a.id } });
        setAds((m) => ({ ...m, [a.id]: res.data.ads || [] }));
      } catch (e) { toast.error("Failed to load ads"); }
    }
  };

  const runForecast = async () => {
    if (!account) return;
    setForecasting(true);
    try {
      const res = await axios.post(`${API}/meta/ad-accounts/${account}/ai-forecast`, null, { params: { date_preset: datePreset } });
      setAiForecast(res.data.narrative || "");
    } catch (e) { toast.error(e.response?.data?.detail || "AI forecast unavailable"); }
    finally { setForecasting(false); }
  };

  const loadBreakdown = useCallback(async () => {
    if (!account) return;
    setLoadingBreakdown(true);
    try {
      const res = await axios.get(`${API}/meta/ad-accounts/${account}/insights`, { params: { level: "account", date_preset: datePreset, breakdown: breakdownType } });
      const rows = (res.data.insights || []).map((r) => ({
        label: r[breakdownType] || "—",
        spend: Math.round(parseFloat(r.spend || 0)),
        ctr: parseFloat(r.ctr || 0),
      })).filter((r) => r.spend > 0 || r.ctr > 0);
      setBreakdownRows(rows);
    } catch (e) { setBreakdownRows([]); }
    finally { setLoadingBreakdown(false); }
  }, [account, datePreset, breakdownType]);

  useEffect(() => { if (status?.connected && account && tab === "overview") loadBreakdown(); }, [status, account, tab, breakdownType, datePreset, loadBreakdown]);

  const saveEdit = async () => {
    const payload = {};
    if (editObj.name != null) payload.name = editObj.name;
    if (editObj.budget) payload.daily_budget = Number(editObj.budget);
    try {
      await axios.post(`${API}/meta/objects/${editObj.id}/update`, payload);
      toast.success("Updated");
      setEditObj(null);
      // refresh the relevant list
      if (editObj.kind === "campaign") loadData();
      if (editObj.kind === "adset" && editObj.parentId) {
        const res = await axios.get(`${API}/meta/ad-accounts/${account}/adsets`, { params: { campaign_id: editObj.parentId } });
        setAdsets((m) => ({ ...m, [editObj.parentId]: res.data.adsets || [] }));
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  if (loading) return <div className="loading" data-testid="meta-loading"><div className="spinner"></div></div>;

  const Header = (
    <div className="page-header">
      <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <span style={{ display: "inline-flex", padding: "8px", borderRadius: "12px",
          background: "linear-gradient(135deg,#1877F2 0%,#C13584 100%)", color: "#fff" }}>
          <Megaphone size={22} />
        </span>
        Meta Ads
        <span style={{ fontSize: "11px", fontWeight: 700, color: "#7c3aed", background: "#ede9fe",
          padding: "3px 10px", borderRadius: "12px" }}>BETA</span>
      </h1>
      <p className="page-description">
        Manage your Facebook &amp; Instagram Ads — view campaigns and performance in one place.
      </p>
    </div>
  );

  // Not configured yet (admin hasn't added Meta app credentials)
  if (!config?.configured) {
    return (
      <div data-testid="meta-ads-page">
        {Header}
        <div className="content-card" data-testid="meta-not-configured" style={{ textAlign: "center", padding: "48px 24px" }}>
          <Rocket size={40} style={{ color: "#1877F2", margin: "0 auto 16px" }} />
          <h2 className="card-title" style={{ marginBottom: "8px" }}>Meta Ads is almost ready</h2>
          <p className="page-description" style={{ maxWidth: "480px", margin: "0 auto" }}>
            This feature is in beta. Your administrator needs to finish connecting the Meta app before you can link your ad accounts.
          </p>
        </div>
      </div>
    );
  }

  // Configured but not connected
  if (!status?.connected) {
    return (
      <div data-testid="meta-ads-page">
        {Header}
        <div className="content-card" data-testid="meta-connect-card" style={{ textAlign: "center", padding: "48px 24px" }}>
          <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginBottom: "20px" }}>
            <Facebook size={36} style={{ color: "#1877F2" }} />
            <Instagram size={36} style={{ color: "#C13584" }} />
          </div>
          <h2 className="card-title" style={{ marginBottom: "8px" }}>Connect your Meta Ads</h2>
          <p className="page-description" style={{ maxWidth: "460px", margin: "0 auto 24px" }}>
            Securely link your Facebook &amp; Instagram Ads account to view your campaigns, ad sets, ads and performance insights right inside Revo Digital.
          </p>
          <Button data-testid="meta-connect-btn" onClick={connect} disabled={connecting}
            style={{ background: "linear-gradient(135deg,#1877F2 0%,#4f46e5 100%)", fontSize: "15px", padding: "12px 28px" }}>
            {connecting ? <><Loader2 size={16} className="spin" style={{ marginRight: "8px" }} /> Opening Meta…</> : <><Link2 size={16} style={{ marginRight: "8px" }} /> Connect Meta Ads</>}
          </Button>
          {status?.expired && (
            <p style={{ marginTop: "14px", fontSize: "13px", color: "#b45309" }}>Your previous connection expired — please reconnect.</p>
          )}
        </div>
      </div>
    );
  }

  // Connected — overview
  const totals = {
    spend: sumField(insights, "spend"),
    impressions: sumField(insights, "impressions"),
    reach: sumField(insights, "reach"),
    clicks: sumField(insights, "clicks"),
  };
  const avgCtr = totals.impressions ? (totals.clicks / totals.impressions) * 100 : 0;
  const avgCpc = totals.clicks ? totals.spend / totals.clicks : 0;
  const accounts = status.ad_accounts || [];

  const chartData = (() => {
    if (!trend) return [];
    const d = (trend.daily || []).map((p) => ({ date: (p.date || "").slice(5), spend: p.spend, clicks: p.clicks }));
    const f = (trend.forecast || []).map((p) => ({ date: (p.date || "").slice(5), spendF: p.spend, clicksF: p.clicks }));
    if (d.length && f.length) {
      d[d.length - 1] = { ...d[d.length - 1], spendF: d[d.length - 1].spend, clicksF: d[d.length - 1].clicks };
    }
    return [...d, ...f];
  })();
  const statusData = [
    { name: "Active", value: campaigns.filter((c) => (c.effective_status || c.status) === "ACTIVE").length },
    { name: "Paused", value: campaigns.filter((c) => (c.effective_status || c.status) !== "ACTIVE").length },
  ];

  const KPI = ({ label, value, accent }) => (
    <div className="content-card" style={{ padding: "18px", margin: 0 }}>
      <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "6px" }}>{label}</div>
      <div style={{ fontSize: "24px", fontWeight: 800, color: accent || "#0f172a" }}>{value}</div>
    </div>
  );

  return (
    <div data-testid="meta-ads-page">
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ display: "inline-flex", padding: "8px", borderRadius: "12px", background: "linear-gradient(135deg,#1877F2 0%,#C13584 100%)", color: "#fff" }}>
              <Megaphone size={22} />
            </span>
            Meta Ads
            <span style={{ fontSize: "11px", fontWeight: 700, color: "#7c3aed", background: "#ede9fe", padding: "3px 10px", borderRadius: "12px" }}>BETA</span>
          </h1>
          <p className="page-description" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <CheckCircle2 size={14} style={{ color: "#16a34a" }} /> Connected{status.meta_user_name ? ` as ${status.meta_user_name}` : ""}
          </p>
        </div>
        <Button variant="outline" size="sm" data-testid="meta-disconnect-btn" onClick={disconnect}>Disconnect</Button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }} data-testid="meta-tabs">
        {[["overview", "Overview", BarChart3], ["ai", "AI Recommendations", Sparkles], ["activity", "Activity Log", History]].map(([key, label, Icon]) => (
          <button key={key} data-testid={`meta-tab-${key}`} onClick={() => setTab(key)}
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 16px", borderRadius: "10px", fontSize: "14px", fontWeight: 600, cursor: "pointer",
              border: tab === key ? "1px solid #7c3aed" : "1px solid #e2e8f0",
              background: tab === key ? "linear-gradient(135deg,#ede9fe,#dbeafe)" : "#fff",
              color: tab === key ? "#6d28d9" : "#64748b" }}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (<>
      {/* Controls */}
      <div className="content-card" style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-end" }}>
        <div className="form-group" style={{ margin: 0, minWidth: "220px" }}>
          <label className="form-label">Ad account</label>
          <select className="form-select" data-testid="meta-account-select" value={account} onChange={(e) => setAccount(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
          </select>
        </div>
        <div className="form-group" style={{ margin: 0, minWidth: "160px" }}>
          <label className="form-label">Date range</label>
          <select className="form-select" data-testid="meta-date-select" value={datePreset} onChange={(e) => setDatePreset(e.target.value)}>
            {DATE_PRESETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ margin: 0, minWidth: "150px" }}>
          <label className="form-label">Insights level</label>
          <select className="form-select" data-testid="meta-level-select" value={level} onChange={(e) => setLevel(e.target.value)}>
            {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} disabled={loadingData} data-testid="meta-refresh-btn">
          <RefreshCw size={14} style={{ marginRight: "6px" }} /> Refresh
        </Button>
      </div>

      {/* KPI summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: "16px", margin: "16px 0" }} data-testid="meta-kpis">
        <KPI label="Spend" value={fmtMoney(totals.spend)} accent="#1877F2" />
        <KPI label="Impressions" value={fmtNum(totals.impressions)} />
        <KPI label="Reach" value={fmtNum(totals.reach)} />
        <KPI label="Clicks" value={fmtNum(totals.clicks)} />
        <KPI label="CTR" value={`${avgCtr.toFixed(2)}%`} accent="#C13584" />
        <KPI label="Avg. CPC" value={fmtMoney(avgCpc)} />
      </div>

      {/* Performance trend + forecast */}
      <div className="content-card" data-testid="meta-trend-card">
        <div className="card-header"><h2 className="card-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}><BarChart3 size={18} /> Performance trend &amp; 7-day forecast</h2></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: "20px", alignItems: "center" }}>
          <div>
            {chartData.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="spendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1877F2" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#1877F2" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area yAxisId="l" type="monotone" dataKey="spend" name="Spend ($)" stroke="#1877F2" fill="url(#spendFill)" strokeWidth={2} />
                  <Area yAxisId="l" type="monotone" dataKey="spendF" name="Forecast spend" stroke="#1877F2" strokeDasharray="5 4" fill="none" strokeWidth={2} />
                  <Line yAxisId="r" type="monotone" dataKey="clicks" name="Clicks" stroke="#C13584" strokeWidth={2} dot={false} />
                  <Line yAxisId="r" type="monotone" dataKey="clicksF" name="Forecast clicks" stroke="#C13584" strokeDasharray="5 4" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
                No daily spend in this range — pick a wider date range or activate a campaign to see trends.
              </div>
            )}
          </div>
          <div>
            <div style={{ display: "grid", gap: "12px", marginBottom: "16px" }}>
              <div style={{ background: "#eff6ff", borderRadius: "12px", padding: "14px" }}>
                <div style={{ fontSize: "11px", color: "#64748b" }}>Projected spend (next 7d)</div>
                <div style={{ fontSize: "22px", fontWeight: 800, color: "#1877F2" }} data-testid="forecast-spend">{fmtMoney(trend?.projected_spend_7d)}</div>
              </div>
              <div style={{ background: "#fdf2f8", borderRadius: "12px", padding: "14px" }}>
                <div style={{ fontSize: "11px", color: "#64748b" }}>Projected clicks (next 7d)</div>
                <div style={{ fontSize: "22px", fontWeight: 800, color: "#C13584" }} data-testid="forecast-clicks">{fmtNum(trend?.projected_clicks_7d)}</div>
              </div>
            </div>
            <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "6px", textAlign: "center" }}>Campaign status</div>
            <ResponsiveContainer width="100%" height={120}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={30} outerRadius={50} paddingAngle={2}>
                  {statusData.map((s, i) => <Cell key={i} fill={i === 0 ? "#16a34a" : "#cbd5e1"} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* AI narrative forecast */}
        <div style={{ marginTop: "16px", borderTop: "1px dashed #e2e8f0", paddingTop: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
            <div style={{ fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
              <Sparkles size={16} style={{ color: "#7c3aed" }} /> AI forecast insight
            </div>
            <Button size="sm" variant="outline" data-testid="ai-forecast-btn" onClick={runForecast} disabled={forecasting}>
              {forecasting ? <><Loader2 size={13} className="spin" style={{ marginRight: "6px" }} /> Thinking…</> : "Explain this trend"}
            </Button>
          </div>
          {aiForecast && (
            <p data-testid="ai-forecast-text" style={{ marginTop: "10px", background: "#faf5ff", borderLeft: "3px solid #7c3aed", borderRadius: "8px", padding: "12px 14px", fontSize: "13px", color: "#334155", lineHeight: 1.6 }}>
              {aiForecast}
            </p>
          )}
        </div>
      </div>

      {/* Breakdown charts */}
      <div className="content-card" data-testid="meta-breakdown-card">
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
          <h2 className="card-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}><BarChart3 size={18} /> Breakdowns</h2>
          <select className="form-select" data-testid="breakdown-select" style={{ maxWidth: "200px" }} value={breakdownType} onChange={(e) => setBreakdownType(e.target.value)}>
            <option value="publisher_platform">By placement</option>
            <option value="age">By age</option>
            <option value="gender">By gender</option>
            <option value="device_platform">By device</option>
          </select>
        </div>
        {loadingBreakdown ? (
          <div style={{ textAlign: "center", padding: "24px" }}><Loader2 size={18} className="spin" /></div>
        ) : breakdownRows.length === 0 ? (
          <div style={{ textAlign: "center", color: "#94a3b8", padding: "24px" }}>No breakdown data for this range.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={breakdownRows} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="l" dataKey="spend" name="Spend ($)" fill="#1877F2" radius={[6, 6, 0, 0]} />
              <Bar yAxisId="r" dataKey="ctr" name="CTR (%)" fill="#C13584" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Insights table */}
      <div className="content-card" data-testid="meta-insights-card">
        <div className="card-header"><h2 className="card-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}><BarChart3 size={18} /> {LEVELS.find(l => l[0] === level)?.[1]} performance</h2></div>
        <div style={{ overflowX: "auto" }}>
          <table className="data-table" style={{ width: "100%" }} data-testid="meta-insights-table">
            <thead><tr>
              <th style={{ textAlign: "left" }}>Name</th>
              <th style={{ textAlign: "right" }}>Spend</th>
              <th style={{ textAlign: "right" }}>Impr.</th>
              <th style={{ textAlign: "right" }}>Clicks</th>
              <th style={{ textAlign: "right" }}>CTR</th>
              <th style={{ textAlign: "right" }}>CPC</th>
            </tr></thead>
            <tbody>
              {loadingData ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: "24px" }}><Loader2 size={18} className="spin" /></td></tr>
              ) : insights.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center", color: "#94a3b8", padding: "24px" }}>No data for this range.</td></tr>
              ) : insights.map((r, i) => (
                <tr key={i}>
                  <td>{r.campaign_name || r.adset_name || r.ad_name || "Account total"}</td>
                  <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtMoney(r.spend)}</td>
                  <td style={{ textAlign: "right" }}>{fmtNum(r.impressions)}</td>
                  <td style={{ textAlign: "right" }}>{fmtNum(r.clicks)}</td>
                  <td style={{ textAlign: "right" }}>{r.ctr ? `${Number(r.ctr).toFixed(2)}%` : "—"}</td>
                  <td style={{ textAlign: "right" }}>{fmtMoney(r.cpc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Campaigns with pause/resume */}
      <div className="content-card" data-testid="meta-campaigns-card">
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 className="card-title">Campaigns ({campaigns.length})</h2>
          <Button size="sm" data-testid="meta-create-ad-btn" onClick={() => setShowWizard(true)}
            style={{ background: "linear-gradient(135deg,#7c3aed,#4f46e5)" }}>
            <Plus size={15} style={{ marginRight: "6px" }} /> Create Ad
          </Button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="data-table" style={{ width: "100%" }} data-testid="meta-campaigns-table">
            <thead><tr>
              <th style={{ textAlign: "left" }}>Campaign</th>
              <th style={{ textAlign: "left" }}>Objective</th>
              <th style={{ textAlign: "left" }}>Status</th>
              <th style={{ textAlign: "right" }}>Action</th>
            </tr></thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: "center", color: "#94a3b8", padding: "24px" }}>No campaigns in this account yet.</td></tr>
              ) : campaigns.map((c) => {
                const active = (c.effective_status || c.status) === "ACTIVE";
                const expanded = expandedCamp === c.id;
                return (
                  <Fragment key={c.id}>
                  <tr data-testid={`meta-campaign-${c.id}`}>
                    <td style={{ fontWeight: 600 }}>
                      <button onClick={() => toggleCampaign(c)} data-testid={`expand-camp-${c.id}`}
                        style={{ background: "none", border: "none", cursor: "pointer", marginRight: "6px", verticalAlign: "-2px", color: "#7c3aed" }}>
                        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                      {c.name}
                    </td>
                    <td style={{ color: "#64748b" }}>{(c.objective || "").replace("OUTCOME_", "").replace("_", " ")}</td>
                    <td>
                      <span style={{ fontSize: "11px", fontWeight: 700, padding: "3px 10px", borderRadius: "12px",
                        background: active ? "#dcfce7" : "#f1f5f9", color: active ? "#16a34a" : "#64748b" }}>
                        {c.effective_status || c.status}
                      </span>
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <Button variant="outline" size="sm" onClick={() => setEditObj({ id: c.id, kind: "campaign", name: c.name, budget: c.daily_budget ? (c.daily_budget / 100) : "" })} style={{ marginRight: "6px" }} data-testid={`edit-camp-${c.id}`}>
                        <Pencil size={13} />
                      </Button>
                      <Button variant="outline" size="sm" data-testid={`meta-toggle-${c.id}`} onClick={() => toggleStatus(c)}>
                        {active ? <><Pause size={13} style={{ marginRight: "4px" }} /> Pause</> : <><Play size={13} style={{ marginRight: "4px" }} /> Resume</>}
                      </Button>
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={4} style={{ background: "#f8fafc", padding: "12px 16px" }}>
                        {!adsets[c.id] ? (
                          <div style={{ color: "#94a3b8", fontSize: "13px" }}><Loader2 size={14} className="spin" /> Loading ad sets…</div>
                        ) : adsets[c.id].length === 0 ? (
                          <div style={{ color: "#94a3b8", fontSize: "13px" }}>No ad sets in this campaign.</div>
                        ) : adsets[c.id].map((a) => {
                          const aActive = (a.effective_status || a.status) === "ACTIVE";
                          const aExp = expandedAdset === a.id;
                          return (
                            <div key={a.id} data-testid={`adset-${a.id}`} style={{ borderLeft: "2px solid #c4b5fd", paddingLeft: "12px", margin: "6px 0" }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <button onClick={() => toggleAdset(a)} data-testid={`expand-adset-${a.id}`} style={{ background: "none", border: "none", cursor: "pointer", color: "#7c3aed" }}>
                                    {aExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  </button>
                                  <span style={{ fontWeight: 600, fontSize: "13px" }}>{a.name}</span>
                                  <span style={{ fontSize: "11px", color: "#64748b" }}>
                                    {a.daily_budget ? `· $${(a.daily_budget / 100).toFixed(0)}/day` : a.lifetime_budget ? `· $${(a.lifetime_budget / 100).toFixed(0)} total` : ""}
                                    {a.optimization_goal ? ` · ${a.optimization_goal.replace(/_/g, " ").toLowerCase()}` : ""}
                                  </span>
                                  <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "10px", background: aActive ? "#dcfce7" : "#f1f5f9", color: aActive ? "#16a34a" : "#64748b" }}>{a.effective_status || a.status}</span>
                                </div>
                                <div style={{ whiteSpace: "nowrap" }}>
                                  <Button variant="outline" size="sm" onClick={() => setEditObj({ id: a.id, kind: "adset", parentId: c.id, name: a.name, budget: a.daily_budget ? (a.daily_budget / 100) : "" })} style={{ marginRight: "6px" }} data-testid={`edit-adset-${a.id}`}><Pencil size={12} /></Button>
                                  <Button variant="outline" size="sm" onClick={() => setObjStatus(a.id, a.effective_status || a.status, "adset", c.id)} data-testid={`toggle-adset-${a.id}`}>{aActive ? <Pause size={12} /> : <Play size={12} />}</Button>
                                </div>
                              </div>
                              {aExp && (
                                <div style={{ marginLeft: "18px", marginTop: "6px" }}>
                                  {!ads[a.id] ? (
                                    <div style={{ color: "#94a3b8", fontSize: "12px" }}><Loader2 size={12} className="spin" /> Loading ads…</div>
                                  ) : ads[a.id].length === 0 ? (
                                    <div style={{ color: "#94a3b8", fontSize: "12px" }}>No ads in this ad set.</div>
                                  ) : ads[a.id].map((ad) => {
                                    const adActive = (ad.effective_status || ad.status) === "ACTIVE";
                                    return (
                                      <div key={ad.id} data-testid={`ad-${ad.id}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", padding: "4px 0" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                          {ad.creative?.thumbnail_url && <img src={ad.creative.thumbnail_url} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: "cover" }} />}
                                          <span style={{ fontSize: "13px" }}>{ad.name}</span>
                                          <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "10px", background: adActive ? "#dcfce7" : "#f1f5f9", color: adActive ? "#16a34a" : "#64748b" }}>{ad.effective_status || ad.status}</span>
                                        </div>
                                        <Button variant="outline" size="sm" onClick={() => setObjStatus(ad.id, ad.effective_status || ad.status, "ad", a.id)} data-testid={`toggle-ad-${ad.id}`}>{adActive ? <Pause size={12} /> : <Play size={12} />}</Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      </>)}

      {/* AI Recommendations */}
      {tab === "ai" && (
        <div data-testid="meta-ai-tab">
          <div className="content-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h2 className="card-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Sparkles size={18} style={{ color: "#7c3aed" }} /> AI Optimization
              </h2>
              <p className="page-description" style={{ margin: 0 }}>Claude analyzes your performance and suggests safe, field-level changes you approve.</p>
            </div>
            <Button data-testid="meta-analyze-btn" onClick={runAnalysis} disabled={analyzing}
              style={{ background: "linear-gradient(135deg,#7c3aed,#4f46e5)" }}>
              {analyzing ? <><Loader2 size={16} className="spin" style={{ marginRight: "8px" }} /> Analyzing…</> : <><Sparkles size={16} style={{ marginRight: "8px" }} /> Analyze with AI</>}
            </Button>
          </div>

          {aiSummary && (
            <div className="content-card" data-testid="ai-summary" style={{ background: "#faf5ff", borderLeft: "3px solid #7c3aed" }}>
              <strong style={{ color: "#6d28d9" }}>Summary</strong>
              <p style={{ margin: "6px 0 0", color: "#334155" }}>{aiSummary}</p>
            </div>
          )}

          {recs.filter((r) => ["pending", "applied"].includes(r.status)).length === 0 ? (
            <div className="content-card" style={{ textAlign: "center", color: "#94a3b8", padding: "40px" }}>
              No recommendations yet — click <strong>Analyze with AI</strong> to generate some.
            </div>
          ) : recs.filter((r) => ["pending", "applied"].includes(r.status)).map((r) => {
            const riskColor = { low: "#16a34a", medium: "#b45309", high: "#dc2626" }[r.risk] || "#64748b";
            const riskBg = { low: "#dcfce7", medium: "#fef3c7", high: "#fee2e2" }[r.risk] || "#f1f5f9";
            return (
              <div className="content-card" key={r.id} data-testid={`ai-rec-${r.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "15px" }}>{r.title}</div>
                    <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>{r.level} · {r.object_name}</div>
                  </div>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "11px", fontWeight: 700, padding: "3px 10px", borderRadius: "12px", background: riskBg, color: riskColor }}>
                    <ShieldAlert size={12} /> {r.risk} risk
                  </span>
                </div>
                <p style={{ fontSize: "13px", color: "#475569", marginTop: "10px" }}><strong>Issue:</strong> {r.issue}</p>
                <p style={{ fontSize: "13px", color: "#475569" }}><strong>Expected impact:</strong> {r.expected_impact}</p>
                <div style={{ margin: "10px 0", display: "flex", flexDirection: "column", gap: "6px" }}>
                  {(r.changes || []).map((c, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", background: "#f8fafc", borderRadius: "8px", padding: "8px 12px" }}>
                      <span style={{ fontWeight: 600, minWidth: "120px" }}>{c.label || c.field}</span>
                      <span style={{ color: "#94a3b8", textDecoration: "line-through" }}>{c.current ?? "—"}</span>
                      <ArrowRightMini />
                      <span style={{ color: "#16a34a", fontWeight: 700 }}>{c.proposed}</span>
                    </div>
                  ))}
                </div>
                {r.rationale && <p style={{ fontSize: "12px", color: "#94a3b8" }}>{r.rationale}</p>}
                <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                  {r.status === "applied" ? (
                    <Button variant="outline" size="sm" data-testid={`ai-rollback-${r.id}`} onClick={() => rollbackRec(r)}>
                      <Undo2 size={13} style={{ marginRight: "6px" }} /> Roll back
                    </Button>
                  ) : (
                    <>
                      <Button size="sm" data-testid={`ai-apply-${r.id}`} onClick={() => applyRec(r)} style={{ background: "linear-gradient(135deg,#16a34a,#059669)" }}>
                        <CheckCircle2 size={13} style={{ marginRight: "6px" }} /> Approve &amp; apply
                      </Button>
                      <Button variant="outline" size="sm" data-testid={`ai-dismiss-${r.id}`} onClick={() => dismissRec(r)}>
                        <X size={13} style={{ marginRight: "4px" }} /> Dismiss
                      </Button>
                    </>
                  )}
                  {r.status === "applied" && <span style={{ alignSelf: "center", fontSize: "12px", color: "#16a34a", fontWeight: 600 }}>✓ Applied</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Activity Log */}
      {tab === "activity" && (
        <div className="content-card" data-testid="meta-activity-tab">
          <div className="card-header"><h2 className="card-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}><History size={18} /> Activity Log</h2></div>
          {activity.length === 0 ? (
            <div style={{ textAlign: "center", color: "#94a3b8", padding: "32px" }}>No changes logged yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {activity.map((a, i) => (
                <div key={i} data-testid={`activity-${i}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#f8fafc", borderRadius: "8px" }}>
                  <div>
                    <span style={{ fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "10px", background: a.source === "ai" ? "#ede9fe" : "#e0f2fe", color: a.source === "ai" ? "#6d28d9" : "#0369a1", marginRight: "8px" }}>
                      {a.source === "ai" ? "AI" : "Manual"}
                    </span>
                    <span style={{ fontWeight: 600, color: "#0f172a", fontSize: "13px" }}>{(a.action || "").replace(/_/g, " ")}</span>
                    {a.title && <span style={{ color: "#64748b", fontSize: "13px" }}> — {a.title}</span>}
                    <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>
                      {a.object_id ? `Object ${a.object_id} · ` : ""}{JSON.stringify(a.changes || a.new_value || a.restored || a.created || {})}
                    </div>
                  </div>
                  <span style={{ fontSize: "11px", color: "#94a3b8", whiteSpace: "nowrap" }}>{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editObj && (
        <div data-testid="meta-edit-modal" onClick={() => setEditObj(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: "16px", padding: "24px", maxWidth: "420px", width: "100%" }}>
            <h3 style={{ fontSize: "17px", fontWeight: 800, marginBottom: "16px" }}>Edit {editObj.kind}</h3>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="form-input" data-testid="edit-name" value={editObj.name || ""} onChange={(e) => setEditObj({ ...editObj, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Daily budget (USD)</label>
              <input className="form-input" type="number" min="1" step="1" data-testid="edit-budget" value={editObj.budget || ""} onChange={(e) => setEditObj({ ...editObj, budget: e.target.value })} placeholder="e.g. 25" />
              <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>Leave blank to keep the current budget.</div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
              <Button variant="outline" onClick={() => setEditObj(null)}>Cancel</Button>
              <Button onClick={saveEdit} data-testid="edit-save" style={{ background: "linear-gradient(135deg,#7c3aed,#4f46e5)" }}>Save</Button>
            </div>
          </div>
        </div>
      )}

      {showWizard && (
        <CreateAdWizard accountId={account} onClose={() => setShowWizard(false)} onCreated={() => loadData()} />
      )}
    </div>
  );
}

const ArrowRightMini = () => (
  <span style={{ color: "#94a3b8" }}>→</span>
);
